import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useCreateNotebook } from '@/hooks';
import { TemplatePicker } from '@/components/notebooks';
import type { PaperSize, PageTemplate } from '@/types/notebook';
import { brandColors } from '@/theme/colors';

type CreateNotebookDialogProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: (id: string) => void;
};

export const CreateNotebookDialog = ({
  open,
  onClose,
  onSuccess,
}: CreateNotebookDialogProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [paperSize, setPaperSize] = useState<PaperSize>('a4');
  const [template, setTemplate] = useState<PageTemplate>('ruled-college');
  const [error, setError] = useState('');

  const createMutation = useCreateNotebook();

  const handleCreate = async () => {
    setError('');

    if (!name.trim()) {
      setError('Please enter a notebook name');
      return;
    }

    try {
      const id = await createMutation.mutateAsync({
        name: name.trim(),
        description: description.trim(),
        paperSize,
        defaultTemplate: template,
      });
      handleReset();
      onClose();
      onSuccess?.(id);
    } catch (err) {
      setError('Failed to create notebook');
    }
  };

  const handleReset = () => {
    setName('');
    setDescription('');
    setPaperSize('a4');
    setTemplate('ruled-college');
    setError('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && name.trim()) {
      e.preventDefault();
      handleCreate();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: brandColors.purple }}>Create Notebook</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Notebook Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            fullWidth
            required
            placeholder="e.g., Meeting Notes, Journal..."
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            placeholder="Add a description for your notebook..."
          />

          <Box sx={{ mt: 1 }}>
            <TemplatePicker
              selectedTemplate={template}
              onTemplateSelect={setTemplate}
              selectedPaperSize={paperSize}
              onPaperSizeSelect={setPaperSize}
              showPaperSize
            />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={createMutation.isPending || !name.trim()}
          startIcon={<Add />}
          sx={{
            bgcolor: brandColors.purple,
            '&:hover': { bgcolor: brandColors.purpleDeep },
          }}
        >
          Create Notebook
        </Button>
      </DialogActions>
    </Dialog>
  );
};
